import React, { createContext, useContext, ReactNode } from 'react';
import {
    useDivisionalManagerSurveyProgress,
    DivisionalManagerItemStateMap,
} from '../hooks/useDivisionalManagerSurveyProgress';
import {
    SurveyItemRow,
    DivisionalManagerItemState,
} from '../pages/price-surveys/types';

interface DivisionalManagerSurveyProgressContextValue {
    itemStates: DivisionalManagerItemStateMap;
    getItemState: (itemId: string) => DivisionalManagerItemState | undefined;
    markForPcr: (itemId: string, proposedPrice?: number) => void;
    markAsNA: (itemId: string, reason: string | null) => void;
    setProposedPrice: (itemId: string, price: number | null) => void;
    setNaReason: (itemId: string, reason: string | null) => void;
    clearItemState: (itemId: string) => void;
    submitErrorIds: string[];
    getSubmitErrorMessage: (itemId: string) => string | undefined;
    allItemsActioned: boolean;
    completionPercent: number;
    hasChanges: boolean;
    changedCount: number;
    saveProgress: () => Promise<boolean>;
    submitSurvey: () => Promise<void>;
    isSaving: boolean;
    isSubmitting: boolean;
}

const DivisionalManagerSurveyProgressContext =
    createContext<DivisionalManagerSurveyProgressContextValue | null>(null);

interface DivisionalManagerSurveyProgressProviderProps {
    children: ReactNode;
    surveyId: string;
    surveyItems: SurveyItemRow[];
    onSuccess?: () => void | Promise<void>;
}

export function DivisionalManagerSurveyProgressProvider({
    children,
    surveyId,
    surveyItems,
    onSuccess,
}: DivisionalManagerSurveyProgressProviderProps) {
    const progress = useDivisionalManagerSurveyProgress(
        surveyId,
        surveyItems,
        onSuccess,
    );

    return (
        <DivisionalManagerSurveyProgressContext.Provider value={progress}>
            {children}
        </DivisionalManagerSurveyProgressContext.Provider>
    );
}

export function useDivisionalManagerSurveyProgressContext() {
    const context = useContext(DivisionalManagerSurveyProgressContext);
    if (!context) {
        throw new Error(
            'useDivisionalManagerSurveyProgressContext must be used within DivisionalManagerSurveyProgressProvider',
        );
    }
    return context;
}

export function useDivisionalManagerSurveyProgressContextOptional() {
    return useContext(DivisionalManagerSurveyProgressContext);
}
